import { useState, useCallback } from "react";
import type { RowSelectionState } from "@tanstack/react-table";

export interface BulkSelectionProps {
  rowSelectionCount: number;
  onShowSelectedOnly: () => void;
  isSelectedOnly: boolean;
}

/**
 * Hook for managing table row selection and bulk actions.
 *
 * Provides all the state and handlers needed for bulk selection patterns,
 * including row selection state, "Show Selected Only" filtering, and deselection.
 *
 * @returns {Object} Bulk selection state and handlers
 * @returns {RowSelectionState} rowSelection - TanStack Table row selection state object
 * @returns {Function} setRowSelection - Updates row selection state
 * @returns {BulkSelectionProps} bulkSelectionProps - Props to spread to TableControls/TableFooter
 * @returns {boolean} isSelectedOnly - Whether "Show Selected Only" mode is active
 * @returns {Function} deselectAll - Clears all selections and exits "Show Selected Only" mode
 * @returns {number} rowSelectionCount - Number of currently selected rows
 *
 * @example
 * // Basic usage
 * const {
 *   rowSelection,
 *   setRowSelection,
 *   bulkSelectionProps,
 *   isSelectedOnly
 * } = useBulkSelection();
 *
 * // Filter data when "Show Selected Only" is active
 * const displayData = isSelectedOnly
 *   ? data.filter((row) => rowSelection[row.id])
 *   : data;
 *
 * // Use with Table
 * <Table
 *   data={displayData}
 *   enableRowSelection
 *   rowSelection={rowSelection}
 *   onRowSelectionChange={setRowSelection}
 *   renderHeader={(table) => (
 *     <TableControls
 *       bulkActions={[...]}
 *       {...bulkSelectionProps}
 *       onDeselectAll={deselectAll}
 *       totalRowCount={data.length}
 *       onToggleAllRows={(checked) => table.toggleAllRowsSelected(checked)}
 *       allRowsSelected={table.getIsAllRowsSelected()}
 *       someRowsSelected={table.getIsSomeRowsSelected()}
 *     >
 *       <SearchInput />
 *     </TableControls>
 *   )}
 *   renderFooter={() => (
 *     <TableFooter
 *       pagination={!isSelectedOnly ? createFooterPagination(data) : undefined}
 *       {...bulkSelectionProps}
 *     />
 *   )}
 * />
 */
export function useBulkSelection() {
  const [rowSelection, setRowSelectionRaw] = useState<RowSelectionState>({});
  const [orderedSelectedIds, setOrderedSelectedIds] = useState<string[]>([]);
  const [isSelectedOnly, setIsSelectedOnly] = useState(false);

  const setRowSelection = useCallback(
    (updaterOrValue: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)) => {
      setRowSelectionRaw((old) => {
        const newValue = typeof updaterOrValue === "function" ? updaterOrValue(old) : updaterOrValue;

        setOrderedSelectedIds((prevOrder) => {
          const newOrder = prevOrder.filter((id) => newValue[id]);
          const newIds = Object.keys(newValue).filter((id) => newValue[id] && !prevOrder.includes(id));
          return [...newOrder, ...newIds];
        });

        return newValue;
      });
    },
    []
  );

  const rowSelectionCount = Object.keys(rowSelection).length;

  const toggleShowSelectedOnly = useCallback(() => {
    setIsSelectedOnly((prev) => !prev);
  }, []);

  const deselectAll = useCallback(() => {
    setRowSelection({});
    setIsSelectedOnly(false);
  }, [setRowSelection]);

  const bulkSelectionProps: BulkSelectionProps = {
    rowSelectionCount,
    onShowSelectedOnly: toggleShowSelectedOnly,
    isSelectedOnly
  };

  return {
    rowSelection,
    setRowSelection,
    bulkSelectionProps,
    isSelectedOnly,
    deselectAll,
    rowSelectionCount,
    orderedSelectedIds
  };
}
